import React, { useCallback, useMemo } from 'react';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import bash from 'react-syntax-highlighter/dist/cjs/languages/hljs/bash';
import javascript from 'react-syntax-highlighter/dist/cjs/languages/hljs/javascript';
import json from 'react-syntax-highlighter/dist/cjs/languages/hljs/json';
import kotlin from 'react-syntax-highlighter/dist/cjs/languages/hljs/kotlin';
import yaml from 'react-syntax-highlighter/dist/cjs/languages/hljs/yaml';
import solidity from 'react-syntax-highlighter/dist/cjs/languages/prism/solidity';
import { default as SyntaxHighlighterPrism } from 'react-syntax-highlighter/dist/cjs/prism-light';
import atomOneDark from 'react-syntax-highlighter/dist/cjs/styles/hljs/atom-one-dark';
import atomDark from 'react-syntax-highlighter/dist/cjs/styles/prism/atom-dark';
import { colors } from '../../../theme/default/color';
import type {
  ContentTypesConfigurationKeys,
  RichTextProps,
  ComponentGroupEmbeddedKeys,
  EmbeddedEntries,
} from '../RichText.types';
import ActionButtonComponent from './ActionButton';
import AnchorComponent from './Anchor';
import CaseStudyTestimonialComponent from './CaseStudyTestimonial';
import CaseStudyTestimonialInline from './CaseStudyTestimonialInline';
import CurrentOpeningsComponent from './CurrentOpenings';
import DownloadButtonComponent from './DownloadButton';
import Headline from './Headline';
import IconCardsCollectionComponent from './IconCardsCollection';
import ImageComponent from './Image';
import ImagesGridComponent from './ImagesGrid';
import ImagesSliderComponent from './ImagesSlider';
import InstagramPosts from './InstagramPosts';
import LinkImageComponent from './LinkImage';
import PersonCardComponent from './PersonCard';
import TextBlockGridComponent from './TextBlockGrid';
import TilesCollectionComponent from './TilesCollection';
import ReactMarkdown from 'react-markdown';
import { contentTypesConfiguration } from '../RichText.types';
import remarkGfm from 'remark-gfm';
import { Box, Theme, Typography, styled, useTheme } from '@mui/material';
import pluralize from 'pluralize';

import clsx from 'clsx';
import {
  ActionButton,
  CaseStudyTestimonial,
  CurrentOpening,
  DownloadButton,
  ImageSlider,
  InstagramPost,
  TextBlockGrid,
  ImagesGrid,
  UploadFile,
  Maybe,
  Image,
  Anchor,
  PersonCard,
  LinkImage,
  TilesCollection,
  IconCardsCollection,
} from '@rumblefish/strapi/src/strapiTypes';
import { ReactMarkdownOptions } from 'react-markdown/lib/react-markdown';

const PREFIX = 'RichTextGlobal';

const classes = {
  root: `${PREFIX}-root`,
};

const Root = styled('div')(({ theme }: { theme: Theme }) => ({
  [`&.${classes.root}`]: {
    color: theme.palette.primary.main,
    padding: '0 20px 0 20px',
    '& pre': {
      fontFamily: 'Consolas,monospace',
      display: 'block',
      height: 'auto',
      overflow: 'auto',
      margin: '0',
      color: theme.palette.text.primary,
      fontSize: '14px',
      '& a': {
        color: colors.white,
      },
    },
    '& pre:has(code)': {
      background: 'transparent !important',
    },
    '& code:not(pre code)': {
      display: 'inline',
      padding: '2px 6px',
      borderRadius: '6px',
      background: 'rgba(255,255,255,0.1)',
      fontFamily: 'Consolas, monospace',
      fontSize: '0.875em',
      color: colors.white,
      whiteSpace: 'pre-wrap',
    },
    '& h3': {
      fontSize: '26px',
      fontWeight: 'bold',
    },
    '& h4': {
      color: colors.white,
      fontSize: '22px',
      fontWeight: 'bold',
    },
    '& ul': {
      fontSize: '16px',
      textAlign: 'left',
      '&.UnorderedDefaultList': {
        '& li': {
          '&:before': {
            content: '""',
            display: 'inline-block',
            width: '9px',
            top: '-2px',
            position: 'relative',
            height: '9px',
            borderRadius: '5px',
            marginRight: '10px',
            background: theme.palette.aquamarineColors?._0,
          },
        },
      },
      '&.UnorderedDashedList': {
        '& li': {
          '&:before': {
            content: '""',
            display: 'inline-block',
            width: '5px',
            marginBottom: '4px',
            position: 'relative',
            height: '1.5px',
            marginRight: '10px',
            background: theme.palette.blueColors?._0,
          },
        },
      },
      '& li': {
        fontSize: '16px',
        listStyle: 'none',
        '& fragment>ul>li': {
          '&:before': {
            content: '"unset !important"',
          },
        },
        '& p': {
          display: 'inline',
        },
      },
    },

    '& ol': {
      '& li::marker': {
        fontWeight: 'bold',
      },
      fontSize: '16px',
      textAlign: 'left',
      '& li': {
        fontSize: '16px',
        '& fragment>ul>li': {
          '&:before': {
            content: '"unset !important"',
          },
        },
        '& p': {
          display: 'inline',
        },
      },
    },
    '& pre code': {
      display: 'block',
      padding: '0',
      whiteSpace: 'pre',
      background: 'transparent !important',
    },

    '& .tableContainer': {
      width: '100%',
      marginTop: '40px',
      marginBottom: '40px',
      overflowX: 'auto',
    },

    ...theme.mixins?.gradientTable?.('small'),
  },
}));

SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('yaml', yaml);
SyntaxHighlighterPrism.registerLanguage('solidity', solidity);
SyntaxHighlighter.registerLanguage('kotlin', kotlin);

export const formatToCompare = (str: string): string | undefined => {
  if (!str) return;
  return str.replace(/\s+/g, '').toLowerCase();
};

const toSlug = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const getTextContent = (node: React.ReactNode): string => {
  if (typeof node === 'string') return node;
  if (Array.isArray(node))
    return (node as React.ReactNode[]).map(getTextContent).join('');
  if (React.isValidElement(node))
    return getTextContent(
      (node.props as { children?: React.ReactNode }).children
    );
  return '';
};

const CODE_BG = '#1a1d27';
const HEADER_BG = '#13151f';

const CodeBlock: React.FC<{ language: string; code: string }> = ({
  language,
  code,
}) => {
  const isSolidity = language === 'solidity';
  return (
    <Box
      sx={{
        borderRadius: '14px',
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.08)',
        my: '1.5rem',
        boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
      }}>
      <Box
        sx={{
          background: HEADER_BG,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          px: 2,
          py: '10px',
        }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: '#ff5f57',
          }}
        />
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: '#febc2e',
          }}
        />
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: '#28c840',
          }}
        />
        {language && (
          <Box
            component="span"
            sx={{
              ml: 'auto',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.05em',
              color: 'rgba(255,255,255,0.35)',
              textTransform: 'lowercase',
              fontFamily: 'Consolas, monospace',
            }}>
            {language}
          </Box>
        )}
      </Box>
      {isSolidity ? (
        <SyntaxHighlighterPrism
          language={language}
          style={atomDark}
          customStyle={{
            margin: 0,
            borderRadius: 0,
            background: CODE_BG,
            fontSize: '14px',
            lineHeight: '1.6',
            padding: '20px',
          }}>
          {code}
        </SyntaxHighlighterPrism>
      ) : (
        <SyntaxHighlighter
          language={language}
          style={atomOneDark}
          customStyle={{
            margin: 0,
            borderRadius: 0,
            background: CODE_BG,
            fontSize: '14px',
            lineHeight: '1.6',
            padding: '20px',
          }}>
          {code}
        </SyntaxHighlighter>
      )}
    </Box>
  );
};

// Helper function to render embedded component based on type
const renderEmbeddedComponent = (
  type: string,
  currentEmbeddedItem: {
    documentId: string;
    images?: Array<Maybe<UploadFile>>;
    language: string;
    body: string;
    style?: string;
  }
) => {
  switch (type) {
    case 'codeSnippet':
      return (
        <CodeBlock
          language={currentEmbeddedItem.language}
          code={currentEmbeddedItem.body}
        />
      );
    case 'caseStudyTestimonial':
      if (currentEmbeddedItem.style === 'normal') {
        return (
          <CaseStudyTestimonialComponent
            model={currentEmbeddedItem as unknown as CaseStudyTestimonial}
          />
        );
      } else {
        return (
          <CaseStudyTestimonialInline
            model={currentEmbeddedItem as unknown as CaseStudyTestimonial}
          />
        );
      }
    case 'imagesGrid':
      return <ImagesGridComponent model={currentEmbeddedItem as ImagesGrid} />;
    case 'textBlockGrid':
      return (
        <TextBlockGridComponent
          model={currentEmbeddedItem as unknown as TextBlockGrid}
        />
      );
    case 'downloadButton':
      return (
        <DownloadButtonComponent
          model={currentEmbeddedItem as DownloadButton}
        />
      );
    case 'actionButton':
      return (
        <ActionButtonComponent
          model={currentEmbeddedItem as unknown as ActionButton}
        />
      );
    case 'imageSlider':
      return (
        <ImagesSliderComponent model={currentEmbeddedItem as ImageSlider} />
      );
    case 'instagramPosts':
      return (
        <InstagramPosts
          model={currentEmbeddedItem as unknown as InstagramPost}
        />
      );
    case 'image':
      return <ImageComponent model={currentEmbeddedItem as unknown as Image} />;
    case 'iconCardsCollection':
      return (
        <IconCardsCollectionComponent
          model={currentEmbeddedItem as unknown as IconCardsCollection}
        />
      );
    case 'anchor':
      return (
        <AnchorComponent model={currentEmbeddedItem as unknown as Anchor} />
      );
    case 'personCard':
      return (
        <PersonCardComponent
          model={currentEmbeddedItem as unknown as PersonCard}
        />
      );
    case 'currentOpenings':
      return (
        <CurrentOpeningsComponent
          model={currentEmbeddedItem as unknown as CurrentOpening}
        />
      );
    case 'linkImage':
      return (
        <LinkImageComponent
          model={currentEmbeddedItem as unknown as LinkImage}
        />
      );
    case 'tilesCollection':
      return (
        <TilesCollectionComponent
          model={currentEmbeddedItem as unknown as TilesCollection}
        />
      );
    default:
      return null;
  }
};

export const RichTextGlobal: React.FC<RichTextProps> = ({
  content,
  embeddedItems,
}) => {
  const theme = useTheme();
  const websiteUrl = process.env.NEXT_PUBLIC_PATH || '';

  // Helper function to handle embedded content
  const handleEmbeddedContent = useCallback(
    (text: string) => {
      const regex = /^\{\{.*\}\}$/;
      const isEmbedded = regex.test(text);

      if (isEmbedded) {
        if (!embeddedItems) return null;
        const [type, contentTypeName] = text.slice(2, -2).split(',');

        const formattedType = pluralize(type)
          .replace(/([a-z])([A-Z])/g, '$1_$2')
          .toLowerCase();

        const oneTypeItems = embeddedItems[
          formattedType as ComponentGroupEmbeddedKeys
        ] as unknown as EmbeddedEntries[] | undefined;

        const contentTypeField = contentTypesConfiguration[
          type as ContentTypesConfigurationKeys
        ]?.field as keyof EmbeddedEntries;

        const currentEmbeddedItem = oneTypeItems?.find((x) => {
          return (
            formatToCompare(x?.[contentTypeField]) ===
            formatToCompare(contentTypeName)
          );
        }) as {
          documentId: string;
          images?: Array<Maybe<UploadFile>>;
          language: string;
          body: string;
          style?: string;
        };

        if (!currentEmbeddedItem) return null;

        return renderEmbeddedComponent(type, currentEmbeddedItem);
      }
      return null;
    },
    [embeddedItems]
  );

  // Custom components for ReactMarkdown
  const components = useMemo(
    (): ReactMarkdownOptions['components'] => ({
      h1: ({ children }: { children: React.ReactNode }) => (
        <Typography variant="h1">{children}</Typography>
      ),
      h2: ({ children }: { children: React.ReactNode }) => {
        const textContent =
          typeof children === 'string' ? children : children?.toString() || '';
        if (textContent.includes('__') || textContent.includes('***')) {
          const cleanText = textContent.replace(/[_*]/g, '');
          return <Headline headline={cleanText} />;
        }
        const id = toSlug(getTextContent(children));
        return (
          <Typography id={id} variant="h2">
            {children}
          </Typography>
        );
      },
      h3: ({ children }: { children: React.ReactNode }) => {
        const id = toSlug(getTextContent(children));
        return (
          <Typography id={id} variant="h3">
            {children}
          </Typography>
        );
      },
      h4: ({ children }: { children: React.ReactNode }) => (
        <Typography variant="h4">{children}</Typography>
      ),
      img: ({ src, alt }: { src?: string; alt?: string }) => (
        <Box
          component="span"
          width={'100%'}
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          sx={{
            img: {
              width: { xs: '100%' },
            },
          }}>
          <img src={src} alt={alt} />
        </Box>
      ),
      a: ({ href, children }: { href?: string; children: React.ReactNode }) => (
        <a
          style={{ color: theme.palette.blueColors?.PLUS_2 }}
          href={href}
          target={href?.startsWith(websiteUrl) ? '_self' : '_blank'}
          rel={href?.startsWith(websiteUrl) ? '' : 'noopener noreferrer'}>
          {children}
        </a>
      ),
      ul: ({ children }: { children: React.ReactNode }) => (
        <ul className={clsx({ UnorderedDefaultList: true })}>{children}</ul>
      ),
      li: ({ children }: { children: React.ReactNode }) => (
        <Typography component={'li'} variant="body1">
          {children}
        </Typography>
      ),
      blockquote: ({ children }: { children: React.ReactNode }) => (
        <Box
          component="blockquote"
          sx={{
            my: '2rem',
            mx: 0,
            pl: 3,
            py: '4px',
            borderLeft: '3px solid',
            borderColor: 'rgba(255,255,255,0.25)',
            '& p': {
              fontSize: '1.2rem !important',
              fontStyle: 'italic',
              lineHeight: '1.7',
              color: 'rgba(255,255,255,0.75) !important',
              margin: '0 !important',
            },
          }}>
          {children}
        </Box>
      ),
      p: ({ children }: { children: React.ReactNode }) => {
        const textContent =
          typeof children === 'string' ? children : children?.toString() || '';

        // Check for embedded content
        const embeddedComponent = handleEmbeddedContent(textContent);
        if (embeddedComponent) {
          return embeddedComponent;
        }

        return (
          <Typography component="p" variant="body1">
            {children}
          </Typography>
        );
      },
      code: ({
        children,
        className,
      }: {
        children: React.ReactNode;
        className?: string;
      }) => {
        const language = className?.replace('language-', '') || '';
        if (language) {
          return (
            <CodeBlock
              language={language}
              code={String(children).replace(/\n$/, '')}
            />
          );
        }
        return <code>{children}</code>;
      },
      pre: ({ children }: { children: React.ReactNode }) => {
        // Handle code blocks wrapped in pre tags
        return <>{children}</>;
      },
      table: ({ children }: { children: React.ReactNode }) => (
        <div className="tableContainer">
          <table>{children}</table>
        </div>
      ),
    }),
    [handleEmbeddedContent, theme.palette.blueColors?.PLUS_2, websiteUrl]
  );

  return (
    <Root className={classes.root}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </Root>
  );
};
